import { InAppState } from "../base/InAppState";

// Registers the <event-source-form> custom element used by the template.
import "../../components/event-source/form";

import template from "./EventSourceListTemplate.html";
import { _lcs, locale } from "../../i18n";

// The label maps below hold German source strings, which are also their
// dictionary keys: module constants are evaluated before I18n exists, so the
// translation happens where they are read.
const REGION_LABELS = {
    saechsische_schweiz : "Sächsische Schweiz"
};

// What a source delivers (core-api: EventSource.CONTENT_TYPE). The label is
// also the unit of a card's record count, so it is plural.
const CONTENT_TYPES = {
    event  : { label: "Veranstaltungen",     icon: "calendar-days" },
    poi    : { label: "Sehenswürdigkeiten",  icon: "landmark" },
    gastro : { label: "Gastronomie",         icon: "utensils" },
    tour   : { label: "Touren",              icon: "route" }
};

const CONTENT_TYPE_FALLBACK = { label: "Inhalte", icon: "layers" };

// Where "show me this feed's records" goes. Missing = that screen does not
// exist yet, and the card leaves the button off rather than offering a dead end.
const RECORD_ROUTES = {
    event  : "/events",
    gastro : "/gastronomy",
    poi    : "/sights",
    tour   : "/tours"
};

const SYNC_LABELS = {
    never   : "noch nie",
    ok      : "erfolgreich",
    partial : "mit Fehlern",
    error   : "fehlgeschlagen"
};

// An import reads a full year from an external API and writes thousands of
// rows — well past the RestApi default of 30 s.
const IMPORT_TIMEOUT_MS = 300000;

/**
 * EventSourceList — "Datenquellen": the external feeds regional content comes
 * from (GET /eventsource).
 *
 * The screen is deliberately named for all regional content, not just events:
 * points of interest and gastronomy will be imported from their own feeds and
 * belong on this list. Today every source is an event feed, which is why the
 * backend resource is still /eventsource and a card's headline count is events.
 *
 * A card list rather than a table: there are a handful of sources, and what an
 * operator needs to see per source is a small block of status — is it enabled,
 * when did it last run, what did that run do, how many events does it own —
 * plus the two actions that matter, "run now" and "edit connection".
 *
 * Running an import is deliberately synchronous: the button waits for the run
 * and reports its statistics. Sources are not created here; they ship with
 * their importer (see the core-api patch that seeds them).
 */
export class EventSourceList extends InAppState
{
    static ROUTE = '/sources';
    static ID = 'event-source-list';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="sources-state"]' );
        this.sourceForm = root.querySelector( '[data-id="evs-form"]' );

        this.sources = [];
        this.sourceById = new Map();
        this.runningId = null;

        this.addEventListener( this.stateEl, "click", ( e ) => this.onAction( e ) );
        this.addEventListener( this.sourceForm, "event-source-saved", () => this.loadSources() );

        await this.loadSources();
    }

    /* ------------------------------- Loading -------------------------------- */

    async loadSources()
    {
        this.showMessage( "layers", _lcs( "Lade Datenquellen …" ), _lcs( "Einen Moment bitte." ) );

        try
        {
            const { result } = await this.app.api.get( "/eventsource" );
            if ( !result || +result.status !== 200 || !Array.isArray( result.data ) )
            {
                throw new Error( "Unexpected /eventsource response." );
            }
            this.sources = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "triangle-alert",
                _lcs( "Datenquellen konnten nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        this.sourceById = new Map();
        this.sources.forEach( ( source ) => this.sourceById.set( String( source.id ), source ) );

        this.renderSources();
    }

    renderSources()
    {
        if ( this.sources.length === 0 )
        {
            this.showMessage(
                "layers",
                _lcs( "Keine Datenquellen eingerichtet" ),
                _lcs( "Quellen werden zusammen mit ihrem Importer ausgeliefert." )
            );
            return;
        }

        this.stateEl.innerHTML = this.sources.map( ( source ) => this._cardHtml( source ) ).join( "" );
    }

    _cardHtml( source )
    {
        const config = source.config || {};
        const stats = source.lastSyncStats || {};
        const isRunning = this.runningId === String( source.id );

        const statusClass = source.isEnabled ? "evs-card__pill--on" : "evs-card__pill--off";
        const syncClass = "evs-card__sync--" + this._escape( source.lastSyncStatus || "never" );
        const contentType = this._contentType( source );

        return '<article class="evs-card" data-source-id="' + this._escape( source.id ) + '">' +
            '<header class="evs-card__head">' +
                '<div class="evs-card__ident">' +
                    '<h3 class="evs-card__title">' + this._escape( source.name || source.key ) + '</h3>' +
                    '<p class="evs-card__key">' +
                        '<span class="evs-card__badge">' + this._escape( String( source.adapter || "" ).toUpperCase() ) + '</span>' +
                        '<span class="evs-card__type evs-card__type--' + this._escape( source.contentType || "event" ) + '">' +
                            '<i class="icon icon--' + contentType.icon + '"></i>' +
                            this._escape( _lcs( contentType.label ) ) +
                        '</span>' +
                        this._escape( source.key ) + ' · ' + this._escape( this._regionLabel( source.region ) ) +
                    '</p>' +
                '</div>' +
                '<span class="evs-card__pill ' + statusClass + '">' +
                    this._escape( source.isEnabled ? _lcs( "aktiv" ) : _lcs( "inaktiv" ) ) +
                '</span>' +
            '</header>' +

            ( source.description
                ? '<p class="evs-card__desc">' + this._escape( source.description ) + '</p>'
                : "" ) +

            '<dl class="evs-card__facts">' +
                this._factHtml( contentType.icon, _lcs( contentType.label ),
                    String( source.recordCount != null ? source.recordCount : "–" ) ) +
                this._factHtml( "clock", _lcs( "Zeitraum" ),
                    _lcs( "{months} Monate", { months: config.months || "–" } ) ) +
                this._factHtml( "navigation", _lcs( "Umkreis" ),
                    config.maxDistanceKm ? config.maxDistanceKm + " km" : _lcs( "ohne Begrenzung" ) ) +
                this._factHtml( "eye", _lcs( "Nach Import" ),
                    config.autoPublish === false ? _lcs( "als Entwurf" ) : _lcs( "veröffentlicht" ) ) +
                this._factHtml( "image", _lcs( "Bilder" ),
                    config.mirrorImages === false
                        ? _lcs( "verlinkt" )
                        : _lcs( "kopiert · max. {px} px", { px: config.imageMaxPixels || 1600 } ) ) +
            '</dl>' +

            '<p class="evs-card__sync ' + syncClass + '">' +
                '<i class="icon icon--' + this._syncIcon( source.lastSyncStatus ) + '"></i>' +
                '<span>' + this._escape( this._syncText( source ) ) + '</span>' +
            '</p>' +

            ( source.lastSyncStatus === "ok" || source.lastSyncStatus === "partial"
                ? '<p class="evs-card__stats">' + this._escape( this._statsText( stats ) ) + '</p>'
                : "" ) +

            ( source.lastSyncMessage
                ? '<p class="evs-card__message">' + this._escape( source.lastSyncMessage ) + '</p>'
                : "" ) +

            '<footer class="evs-card__actions">' +
                '<button type="button" class="btn btn--primary btn--sm" data-action="import" ' +
                        'data-source-id="' + this._escape( source.id ) + '"' +
                        ( source.isEnabled && !isRunning ? "" : " disabled" ) + '>' +
                    '<i class="icon icon--rotate-cw"></i> ' +
                    this._escape( isRunning ? _lcs( "Import läuft …" ) : _lcs( "Jetzt importieren" ) ) +
                '</button>' +
                '<button type="button" class="btn btn--ghost btn--sm" data-action="edit" ' +
                        'data-source-id="' + this._escape( source.id ) + '">' +
                    '<i class="icon icon--pencil"></i> ' + this._escape( _lcs( "Bearbeiten" ) ) +
                '</button>' +
                // Feeds whose list screen exists get a way into it; the POI and
                // tour screens do not exist yet.
                ( RECORD_ROUTES[ source.contentType || "event" ]
                    ? '<button type="button" class="btn btn--ghost btn--sm" data-action="records" ' +
                            'data-source-id="' + this._escape( source.id ) + '">' +
                        '<i class="icon icon--layout-list"></i> ' + this._escape( _lcs( contentType.label ) ) +
                      '</button>'
                    : "" ) +
            '</footer>' +
        '</article>';
    }

    _factHtml( icon, label, value )
    {
        return '<div class="evs-card__fact">' +
            '<dt><i class="icon icon--' + icon + '"></i> ' + this._escape( label ) + '</dt>' +
            '<dd>' + this._escape( value ) + '</dd>' +
        '</div>';
    }

    /* -------------------------------- Actions -------------------------------- */

    onAction( e )
    {
        const btn = e.target.closest( "[data-action]" );
        if ( !btn || btn.disabled ) return;

        const source = this.sourceById.get( btn.getAttribute( "data-source-id" ) );
        if ( !source ) return;

        switch ( btn.getAttribute( "data-action" ) )
        {
            case "edit":    this.sourceForm.openEdit( source ); break;
            case "import":  this.runImport( source ); break;
            case "records": this.openRecords( source ); break;
        }
    }

    /**
     * Jump to this feed's record list, pre-filtered to it. The filter is handed
     * over on the app instance rather than as a query string: the router runs
     * in "url" mode without query support, and a one-shot handoff needs no
     * route of its own.
     */
    openRecords( source )
    {
        const route = RECORD_ROUTES[ source.contentType || "event" ];
        if ( !route )
        {
            return;
        }

        // Each list reads its own handoff property, so arriving at the wrong
        // screen cannot silently apply the other one's filter.
        if ( route === "/events" )
        {
            this.app.pendingEventSourceId = String( source.id );
        }
        else if ( route === "/tours" )
        {
            this.app.pendingTourSourceId = String( source.id );
        }
        else
        {
            this.app.pendingPlaceSourceId = String( source.id );
        }

        this.app.router.redirect( route );
    }

    /**
     * Run one feed and report what it did. The button is locked for the
     * duration — a second run while the first is still writing would fight it
     * over the same rows.
     */
    async runImport( source )
    {
        if ( this.runningId ) return;

        this.runningId = String( source.id );
        this.renderSources();

        try
        {
            const { result } = await this.app.api.post(
                "/eventsource/" + source.id + "/import",
                {},
                { timeout: IMPORT_TIMEOUT_MS }
            );

            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected import response." );
            }

            const stats = result.data && result.data.stats ? result.data.stats : {};
            this.app.view.toast(
                _lcs( "Import abgeschlossen — {stats}", { stats: this._statsText( stats ) } ),
                result.data && result.data.status === "partial" ? "warning" : "success",
                6000
            );
        }
        catch ( err )
        {
            console.error( err );
            this.app.view.toast(
                +err?.status === 502
                    ? _lcs( "Die Quelle konnte nicht abgefragt werden. Details stehen an der Quelle." )
                    : _lcs( "Import fehlgeschlagen. Bitte versuche es später erneut." ),
                "error"
            );
        }
        finally
        {
            this.runningId = null;
            // Reload rather than patch the card: the run wrote its result onto
            // the source, and the event count changed with it.
            await this.loadSources();
        }
    }

    /* ------------------------------- Formatting ------------------------------ */

    _syncText( source )
    {
        const status = SYNC_LABELS[ source.lastSyncStatus ]
            ? _lcs( SYNC_LABELS[ source.lastSyncStatus ] )
            : ( source.lastSyncStatus || _lcs( "unbekannt" ) );
        if ( !source.lastSyncAt )
        {
            return _lcs( "Noch kein Import gelaufen" );
        }
        return _lcs( "Letzter Import {date} · {status}", { date: this._dateText( source.lastSyncAt ), status } );
    }

    _syncIcon( status )
    {
        switch ( status )
        {
            case "ok":      return "circle-check";
            case "partial": return "triangle-alert";
            case "error":   return "triangle-alert";
            default:        return "clock";
        }
    }

    _statsText( stats )
    {
        const parts = [
            _lcs( "{n} neu", { n: stats.created || 0 } ),
            _lcs( "{n} aktualisiert", { n: stats.updated || 0 } ),
            _lcs( "{n} unverändert", { n: stats.unchanged || 0 } ),
            _lcs( "{n} archiviert", { n: stats.archived || 0 } )
        ];

        if ( stats.duplicates )
        {
            parts.push( _lcs( "{n} Dubletten erkannt", { n: stats.duplicates } ) );
        }
        if ( stats.duplicatesReleased )
        {
            parts.push( _lcs( "{n} Dubletten freigegeben", { n: stats.duplicatesReleased } ) );
        }
        if ( stats.imagesCopied )
        {
            parts.push( _lcs( "{n} Bilder kopiert", { n: stats.imagesCopied } ) +
                ( stats.imageBytesStored ? " (" + this._sizeText( stats.imageBytesStored ) + ")" : "" ) );
        }
        // Images the budget did not reach — the next run picks them up, and
        // until then they are served from the provider.
        if ( stats.imagesDeferred )
        {
            parts.push( _lcs( "{n} Bilder offen", { n: stats.imagesDeferred } ) );
        }
        if ( stats.imagesSkippedLicence )
        {
            parts.push( _lcs( "{n} Bilder ohne passende Lizenz", { n: stats.imagesSkippedLicence } ) );
        }
        if ( stats.skippedLocked )
        {
            parts.push( _lcs( "{n} gesperrt", { n: stats.skippedLocked } ) );
        }
        if ( stats.skippedOutOfRange )
        {
            parts.push( _lcs( "{n} außerhalb des Umkreises", { n: stats.skippedOutOfRange } ) );
        }
        if ( stats.failed )
        {
            parts.push( _lcs( "{n} fehlerhaft", { n: stats.failed } ) );
        }
        if ( stats.durationMs )
        {
            parts.push( Math.round( stats.durationMs / 100 ) / 10 + " s" );
        }

        return parts.join( " · " );
    }

    _sizeText( bytes )
    {
        if ( bytes < 1024 * 1024 )
        {
            return Math.round( bytes / 1024 ) + " KB";
        }
        return ( Math.round( bytes / 1024 / 1024 * 10 ) / 10 ) + " MB";
    }

    _dateText( value )
    {
        const date = new Date( value );
        if ( isNaN( date.getTime() ) )
        {
            return "–";
        }
        return _lcs( "{time} Uhr", {
            time : date.toLocaleString( locale(), {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
            } )
        } );
    }

    _regionLabel( region )
    {
        return REGION_LABELS[ region ] ? _lcs( REGION_LABELS[ region ] ) : ( region || "–" );
    }

    /** A source without a content type is an event feed — see patch 13. */
    _contentType( source )
    {
        return CONTENT_TYPES[ source.contentType || "event" ] || CONTENT_TYPE_FALLBACK;
    }

    showMessage( icon, title, text )
    {
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
            '</div>';
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Datenquellen" ),
            subtitle: _lcs( "Externe Schnittstellen, aus denen BELTURO regionale Inhalte importiert" )
        };
    }
}
